import classNames from 'classnames';
import findIndex from 'lodash/findIndex';
import isFunction from 'lodash/isFunction';
import { Component, createRef } from 'react';
import { getElementPosition } from 'helpers/element';
import { FormContext } from './formContext';
import './Form.scss';

const findInvalidFormControl = (formControl) => {
    const { state, isValid } = formControl;

    if (state) {
        return !state.isValid;
    }

    // support for `useForm` consumers
    if (isValid !== 'undefined') {
        return !isValid;
    }

    return false;
};

class Form extends Component {
    constructor(props) {
        super(props);
        this.componentRef = createRef();
        this.formControls = [];
        this.state = { isSubmitted: false, isValid: false };
        this.submit = this.submit.bind(this);
        this.reset = this.reset.bind(this);
        this.attachToForm = this.attachToForm.bind(this);
        this.detachFromForm = this.detachFromForm.bind(this);
        this.validate = this.validate.bind(this);
        this.handleValidity = this.handleValidity.bind(this);
        this.handleScrollToError = this.handleScrollToError.bind(this);
    }

    handleValidity() {
        const isValid = !this.formControls.find(findInvalidFormControl);
        const validityChanged = isValid !== this.state.isValid;

        if (validityChanged) {
            this.setState({ isValid }, () => {
                if (isFunction(this.props.onValidityChange)) {
                    this.props.onValidityChange(isValid);
                }
            });
        }
    }

    handleScrollToError() {
        const formControlWithError = this.formControls.find(findInvalidFormControl);

        if (formControlWithError && formControlWithError.componentRef && formControlWithError.componentRef.current) {
            const { y: position } = getElementPosition(formControlWithError.componentRef.current);
            window.scrollTo({ top: position - (window.innerHeight / 3), behavior: 'smooth' });
        }
    }

    detachFromForm(component) {
        const { formControls } = this;
        const index = findIndex(formControls, formControl => formControl === component);

        formControls.splice(index, 1);
    }

    attachToForm(component) {
        this.formControls.push(component);
    }

    reset() {
        this.formControls.forEach((item) => {
            if (typeof item.reset === 'function') {
                item.reset();
            }
        });

        this.setState({ isSubmitted: false, isValid: false });
    }

    submit(event) {
        const { onSubmit, onSubmitCallback } = this.props;
        const isValid = this.validate();
        const hasSubmitHandler = isFunction(onSubmit);
        this.setState({ isSubmitted: true });

        if (hasSubmitHandler || !isValid) {
            event?.preventDefault();
        }

        if (!isValid) {
            setTimeout(this.handleScrollToError);
        }

        if (hasSubmitHandler && isValid) {
            onSubmit(onSubmitCallback, this.reset);
        }

        return isValid;
    }

    validate() {
        let isValid = true;

        for (const formControl of this.formControls) {
            if (!formControl.validate({})) {
                isValid = false;
            }
        }

        return isValid;
    }

    render() {
        const {
            customClass,
            validateOnInit,
            id,
            children,
            isPseudoBox
        } = this.props;

        return (
            <FormContext.Provider
                // eslint-disable-next-line react/jsx-no-constructed-context-values
                value={{
                    form: {
                        attachToForm: this.attachToForm,
                        detachFromForm: this.detachFromForm,
                        handleValidity: this.handleValidity,
                        validateOnInit: validateOnInit || false,
                        isSubmitted: this.state.isSubmitted
                    }
                }}
            >
                <form
                    ref={this.componentRef}
                    noValidate
                    className={classNames('form', customClass, { 'form--pseudobox': isPseudoBox })}
                    id={id}
                    method="POST"
                    onSubmit={this.submit}
                >
                    {children}
                </form>
            </FormContext.Provider>
        );
    }
}

export default Form;
